import { useQuery } from "@tanstack/react-query";
import { RelativeUrl } from "@adora/shared/utils/environment";
import { getGetHeaders } from "@adora/shared/utils/request";
import { runUnauthorizedHandler } from "@adora/shared/utils/response";
import { snakeToCamel } from "@adora/shared/utils/case-conversions";
import { queryKeys, commonQueryOptions } from "../utils/tanstack-query-helpers";
import type { TokenWallet, WalletServerResponse } from "../types/token-wallet";

/**
 * Fetches a brand's generation-token wallet.
 *
 * A brand with no wallet yet is a normal empty state, so the query resolves to
 * `null` (the UI shows a "Create Wallet" CTA). The backend signals this with a
 * 200 + null body — it deliberately avoids 404 because CloudFront's
 * distribution-wide custom_error_response rewrites any /api/* 404 to a 200
 * index.html page. We still treat a 404 as null for back-compat / defense in
 * depth, and guard against a null/empty body before converting.
 */
async function fetchWallet(brandId: number): Promise<TokenWallet | null> {
  const response = await fetch(`${RelativeUrl}/tokens/wallet?brand_id=${brandId}`, {
    method: "GET",
    headers: await getGetHeaders()
  });

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    if (response.status === 401) runUnauthorizedHandler(response);
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.message || `HTTP Error: ${response.status}`);
  }

  const data = (await response.json()) as WalletServerResponse | null;
  return data ? snakeToCamel(data) : null;
}

export function useTokenWallet(brandId: number | null) {
  return useQuery({
    queryKey: queryKeys.tokenWallet(brandId ?? "none"),
    queryFn: () => fetchWallet(brandId as number),
    enabled: brandId != null && Number.isFinite(brandId),
    ...commonQueryOptions.stable
  });
}
